import sodium from "libsodium-wrappers-sumo";

export interface EncryptedFileResult {
  encryptedData: Uint8Array;
  fileKey: Uint8Array; // 32 bytes — XChaCha20-Poly1305 key
  fileNonce: Uint8Array; // 24 bytes
  digest: string; // BLAKE2b hex digest of encrypted blob (integrity check)
}

/**
 * Encrypt a file with a random XChaCha20-Poly1305 key.
 * Returns the encrypted blob, the key, nonce, and a BLAKE2b digest
 * of the ciphertext for integrity verification after download.
 */
export function encryptFile(plainData: Uint8Array): EncryptedFileResult {
  const fileKey = sodium.randombytes_buf(
    sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES,
  );
  const fileNonce = sodium.randombytes_buf(
    sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES,
  );

  const encryptedData = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    plainData,
    null, // no additional data
    null, // secret nonce (unused)
    fileNonce,
    fileKey,
  );

  const digestBytes = sodium.crypto_generichash(32, encryptedData);
  const digest = sodium.to_hex(digestBytes);

  return { encryptedData, fileKey, fileNonce, digest };
}

/**
 * Decrypt a file encrypted with encryptFile().
 * Throws if the key/nonce are wrong or the ciphertext was tampered with.
 */
export function decryptFile(
  encryptedData: Uint8Array,
  fileKey: Uint8Array,
  fileNonce: Uint8Array,
): Uint8Array {
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null, // secret nonce (unused)
    encryptedData,
    null, // no additional data
    fileNonce,
    fileKey,
  );
}

/**
 * Compute a BLAKE2b hex digest for integrity verification.
 */
export function computeFileDigest(data: Uint8Array): string {
  const digestBytes = sodium.crypto_generichash(32, data);
  return sodium.to_hex(digestBytes);
}
